import { Suspense, type ReactElement } from 'react';
import { Await, useLocation } from 'react-router';
import type { SearchContentData } from '@/lib/api/search-content.server';
import { resolveSearchCategoriesFromContent } from '@/components/search/search-categories-from-content';
import type { ManualCategoriesStatus } from '@/components/search/default-search-viewport';
import Search from './search';

export interface SearchCategoriesProps {
    categoriesStatus: ManualCategoriesStatus;
    categoriesMarkup?: string;
}

interface SearchContentBoundaryProps {
    searchContent?: Promise<SearchContentData>;
    children: (categories: SearchCategoriesProps) => ReactElement;
}

/**
 * Resolves deferred search content once above location-keyed Search remounts.
 * Mirrors the footer Await pattern so category status does not reset on navigation.
 */
export function SearchContentBoundary({ searchContent, children }: SearchContentBoundaryProps): ReactElement {
    if (!searchContent) {
        return children({ categoriesStatus: 'absent' });
    }

    return (
        <Suspense fallback={children({ categoriesStatus: 'pending' })}>
            <Await resolve={searchContent} errorElement={children({ categoriesStatus: 'empty' })}>
                {(data) => {
                    const resolved = resolveSearchCategoriesFromContent(data);
                    return children({
                        categoriesStatus: resolved.status,
                        categoriesMarkup: resolved.categories,
                    });
                }}
            </Await>
        </Suspense>
    );
}

type LocationKeyedSearchProps = SearchCategoriesProps;

/** Isolates location subscription so route changes reset Search input state only. */
export function LocationKeyedSearch({ categoriesStatus, categoriesMarkup }: LocationKeyedSearchProps): ReactElement {
    const location = useLocation();
    return (
        <Search
            key={`${location.pathname}${location.search}`}
            categoriesStatus={categoriesStatus}
            categoriesMarkup={categoriesMarkup}
        />
    );
}
